import React from 'react';
import { Typography, TypographyProps, SxProps, Theme } from '@mui/material';
import { colors, typographyVariants } from '../../tokens';

export interface LabelProps extends Omit<TypographyProps, 'variant'> {
  /**
   * The content to display
   */
  children: React.ReactNode;
  /**
   * Size variant of the label
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether the label is required (adds red asterisk)
   * @default false
   */
  required?: boolean;
  /**
   * Whether to display the label in uppercase
   * Used for form field labels like "NAME", "BIO", "GENRES"
   * @default true
   */
  uppercase?: boolean;
  /**
   * Color variant
   * @default 'primary'
   */
  colorVariant?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Custom styles to apply
   */
  sx?: SxProps<Theme>;
}

/**
 * Label - Text labels for form inputs, UI elements, and metadata
 * 
 * Use cases:
 * - Form field labels
 * - Input labels
 * - Button labels
 * - Metadata labels
 * - Section labels
 * - Status indicators
 * 
 * @example
 * ```tsx
 * <Label>Song Title</Label>
 * <Label required>Email Address</Label>
 * <Label uppercase>Name</Label>
 * <Label uppercase>Genres</Label>
 * <Label size="small" colorVariant="tertiary">Created on</Label>
 * ```
 */
export const Label: React.FC<LabelProps> = ({ 
  children, 
  size = 'medium',
  required = false,
  uppercase = true,
  colorVariant = 'primary',
  sx = {}, 
  ...props 
}) => {
  const sizeMap = {
    small: uppercase ? '0.6875rem' : typographyVariants.labelSmall.fontSize,
    medium: uppercase ? '0.75rem' : typographyVariants.label.fontSize,
    large: uppercase ? '0.8125rem' : '0.95rem',
  };

  const colorMap = {
    primary: uppercase ? 'rgba(255, 255, 255, 0.7)' : colors.text.primary,
    secondary: uppercase ? 'rgba(255, 255, 255, 0.7)' : colors.text.secondary,
    tertiary: uppercase ? 'rgba(255, 255, 255, 0.7)' : colors.text.tertiary,
  };

  const uppercaseStyles = uppercase ? {
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    fontWeight: 600,
    mb: 0.75,
  } : {};

  return (
    <Typography
      sx={{
        fontFamily: typographyVariants.label.fontFamily,
        fontWeight: uppercase ? 600 : typographyVariants.label.fontWeight,
        fontSize: sizeMap[size],
        lineHeight: typographyVariants.label.lineHeight,
        color: colorMap[colorVariant],
        ...uppercaseStyles,
        ...sx,
      }}
      {...props}
    >
      {children}
      {required && (
        <span style={{ color: colors.semantic.error, marginLeft: '4px' }}>*</span>
      )}
    </Typography>
  );
};
